/**
 * @author: subhasish.ghosh@zoom.us
 */

import {
  Box,
  Button,
  Popover,
  Select,
  Skeleton,
  Typography,
} from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import { ListItemText } from "@zoom/react-ui";
import classNames from "classnames";
import { countdownObserver } from "@hooks/useCountdown";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import axiosInstance from "@utils/axiosInstance";
import { isProdEnvironment } from "@utils/utils";
import { removeAllCookies } from "@utils/cookieUtils";
import Link from '@mui/material/Link';

const UserInfoModal = ({ isOpen, onClose, data }) => {
  const copied = useMemo(() => ({ ...data }), [data]);
  const showRoleComponent = useMemo(
    () =>
      !isProdEnvironment() && data?.modifyRole && data?.availableRoles?.length,
    [data]
  );
  const [open, setOpen] = useState(false);
  const [numberOfItems, setNumberOfItems] = useState(0);

  useEffect(() => {
    setOpen(isOpen);
  }, [isOpen]);

  useEffect(() => {
    delete copied?.["actions"];
    delete copied?.["modifyRole"];
    delete copied?.["availableRoles"];
    if (copied?.["role"] === null) {
      delete copied?.["role"];
    }
    setNumberOfItems(Object.keys(copied)?.length || 0);
  }, [copied]);

  const handleClose = () => {
    setOpen(false);
    onClose(false);
  };

  return (
    <Popover
      open={open}
      onClose={handleClose}
      className={classNames(
        "user-info-popover",
        showRoleComponent
          ? "four"
          : numberOfItems === 1
          ? "one"
          : numberOfItems === 2
          ? "two"
          : "three"
      )}
      anchorOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      aria-labelledby="user-info"
    >
      <Box
        sx={{
          position: "absolute",
          top: "5%",
          right: "1%",
          width: 306,
          bgcolor: "background.paper",
          borderRadius: "8px",
          boxShadow: 24,
          p: '20px',
          "& .MuiTooltip-tooltip": {
            backgroundColor: "inherit",
            color: "inherit",
            boxShadow: "none",
          },
        }}
      >
        <Box mt={1} sx={{ margin: 0 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: "bold" }}>
            {data?.name || "Guest"}
          </Typography>
          <Role
            show={showRoleComponent}
            name={data?.name}
            email={data?.email}
            currentRole={data?.role}
            availableRoles={data?.availableRoles}
          />
          {data?.email ? (
            <Box sx={{ display: "flex", gap: "0.5rem" }}>
              <Typography
                variant="body2"
                color="text.secondary"
                className="email"
              >
                Email
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {data?.email}
              </Typography>
            </Box>
          ) : (
            <></>
          )}
          {data?.processOverviewDocUrl ? (
          <Box mt={1}>
            <Link
              variant="body2"
              component="a"
              href={data?.processOverviewDocUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                color: "#0C66E4",
                textDecoration: "none",
                wordBreak: "break-word",
              }}
            >
              Read Process Overview
            </Link>
          </Box>
           ):<></>}
        </Box>
      </Box>
    </Popover>
  );
};

const Role = ({ show, name, email, currentRole, availableRoles }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [items, setItems] = useState([]);
  const [value, setValue] = useState(currentRole || "");
  const [previousValue, setPreviousValue] = useState(currentRole || "");
  const [isRoleChanging, setRoleChanging] = useState(false);

  const handleRoleChange = async (event) => {
    setRoleChanging(true);
    setPreviousValue(value);
    const updatedRole = event.target.value;
    setValue(updatedRole);

    return await axiosInstance
      .post(
        `/operationmonitor/api/v1/user`,
        {
          userId: email,
          role: updatedRole,
          name,
        },
        {
          refetchOnWindowFocus: false,
        }
      )
      .then(() => {
        enqueueSnackbar(`Role change successful. Reloading now...`, {
          variant: "success",
        });
        countdownObserver(() => {
          window.location.reload();
        }, 5);
      })
      .catch(() => {
        setValue(previousValue);
        enqueueSnackbar("Role change failed.", { variant: "error" });
      })
      .finally(() => {
        setRoleChanging(false);
      });
  };

  useEffect(() => {
    setItems(
      availableRoles?.map((item, key) => ({
        label: item,
        key: key,
      }))
    );
  }, [availableRoles]);

  return show ? (
    <>
      <FormControl
        sx={{ margin: "8px 0", padding: 1, minWidth: 150 }}
        size="small"
      >
        <InputLabel id="select_label" className="select_label">
          Role
        </InputLabel>
        {!isRoleChanging ? (
          <Select
            labelId="select_label"
            id="select_label"
            value={value}
            label="Role"
            onChange={handleRoleChange}
            renderValue={(value) =>
              value ? (
                <label style={{ fontSize: "14px", color: "#323539" }}>
                  {value}
                </label>
              ) : (
                <></>
              )
            }
            displayEmpty
          >
            {items.map(({ label, key }) => (
              <MenuItem key={key} value={label}>
                <ListItemText primary={label} />
              </MenuItem>
            ))}
          </Select>
        ) : (
          <Skeleton animation="wave" height="40px" />
        )}
      </FormControl>
    </>
  ) : (
    <Typography variant="body2" color="text.secondary">
      {value || ""}
    </Typography>
  );
};

export default UserInfoModal;
